import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { driverGroupsService } from '../../services/driverGroupsService';
import { driversService } from '../../services/driversService';
import { DriverGroupResponse } from '../../types/driverGroups';
import { OrderAssignedDriverResponse } from '../../types/orders';
import { AdminDriversResponse } from '../../types/drivers';
import Modal from '../modals/Modal';
import './Page.css';

const DriverGroups: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [groups, setGroups] = useState<DriverGroupResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DriverGroupResponse | null>(null);
  const [groupName, setGroupName] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  const [selectedGroup, setSelectedGroup] = useState<DriverGroupResponse | null>(null);
  const [groupDrivers, setGroupDrivers] = useState<OrderAssignedDriverResponse[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversError, setDriversError] = useState('');
  const [driverIdInput, setDriverIdInput] = useState('');
  const [driverActionLoading, setDriverActionLoading] = useState(false);
  const [driverSearchQuery, setDriverSearchQuery] = useState('');
  const [driverSearchResults, setDriverSearchResults] = useState<AdminDriversResponse[]>([]);
  const [driverSearchLoading, setDriverSearchLoading] = useState(false);
  const [driverSearchError, setDriverSearchError] = useState('');

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchGroupDrivers(selectedGroup.id);
    } else {
      setGroupDrivers([]);
    }
    setDriverSearchQuery('');
    setDriverSearchResults([]);
    setDriverIdInput('');
  }, [selectedGroup]);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  useEffect(() => {
    if (!selectedGroup) return;

    const trimmedQuery = driverSearchQuery.trim();
    if (trimmedQuery.length < 2) {
      setDriverSearchResults([]);
      setDriverSearchError('');
      setDriverSearchLoading(false);
      return;
    }

    setDriverSearchLoading(true);
    setDriverSearchError('');

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await driversService.getDrivers({
          searchQuery: trimmedQuery,
          page: 0,
          pageSize: 10,
          applicationStatus: null,
          driverState: null
        });

        if (response.success && response.data) {
          setDriverSearchResults(response.data);
        } else {
          setDriverSearchResults([]);
          setDriverSearchError(response.error || 'Failed to search drivers');
        }
      } catch (error) {
        setDriverSearchResults([]);
        setDriverSearchError('Network error. Please try again.');
      } finally {
        setDriverSearchLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [driverSearchQuery, selectedGroup]);

  const fetchGroups = async () => {
    setLoading(true);
    setPageError('');

    try {
      const response = await driverGroupsService.listGroups();
      if (response.success && response.data) {
        setGroups(response.data);
      } else {
        setPageError(response.error || 'Failed to fetch driver groups');
      }
    } catch (error) {
      setPageError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDrivers = async (groupId: number) => {
    setDriversLoading(true);
    setDriversError('');

    try {
      const response = await driverGroupsService.listDriversInGroup(groupId);
      if (response.success && response.data) {
        setGroupDrivers(response.data);
      } else {
        setDriversError(response.error || 'Failed to fetch group drivers');
      }
    } catch (error) {
      setDriversError('Network error. Please try again.');
    } finally {
      setDriversLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingGroup(null);
    setGroupName('');
    setModalError('');
    setShowModal(true);
  };

  const openEditModal = (group: DriverGroupResponse) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setModalError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setGroupName('');
    setModalError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');

    if (!groupName.trim()) {
      setModalError(t('driverGroups.nameRequired'));
      return;
    }

    setModalLoading(true);

    try {
      if (editingGroup) {
        const response = await driverGroupsService.updateGroup(editingGroup.id, { name: groupName.trim() });
        if (response.success) {
          showSuccess(t('driverGroups.groupUpdated'));
          await fetchGroups();
          if (selectedGroup?.id === editingGroup.id) {
            setSelectedGroup(response.data || editingGroup);
          }
          closeModal();
        } else {
          setModalError(response.error || 'Failed to update driver group');
        }
      } else {
        const response = await driverGroupsService.createGroup({ name: groupName.trim() });
        if (response.success) {
          showSuccess(t('driverGroups.groupCreated'));
          await fetchGroups();
          closeModal();
        } else {
          setModalError(response.error || 'Failed to create driver group');
        }
      }
    } catch (error) {
      setModalError('Network error. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (groupId: number) => {
    if (!window.confirm(t('driverGroups.confirmDelete'))) {
      return;
    }

    setPageError('');

    try {
      const response = await driverGroupsService.deleteGroup(groupId);
      if (response.success) {
        showSuccess(t('driverGroups.groupDeleted'));
        if (selectedGroup?.id === groupId) {
          setSelectedGroup(null);
        }
        await fetchGroups();
      } else {
        setPageError(response.error || 'Failed to delete driver group');
      }
    } catch (error) {
      setPageError('Network error. Please try again.');
    }
  };

  const handleAddDriver = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedGroup) return;

    const driverId = parseInt(driverIdInput, 10);
    if (Number.isNaN(driverId)) {
      setDriversError(t('driverGroups.invalidDriverId'));
      return;
    }

    setDriverActionLoading(true);
    setDriversError('');

    try {
      const response = await driverGroupsService.addDriverToGroup(selectedGroup.id, driverId);
      if (response.success) {
        showSuccess(t('driverGroups.driverAdded'));
        setDriverIdInput('');
        setDriverSearchQuery('');
        setDriverSearchResults([]);
        await fetchGroupDrivers(selectedGroup.id);
      } else {
        setDriversError(response.error || 'Failed to add driver');
      }
    } catch (error) {
      setDriversError('Network error. Please try again.');
    } finally {
      setDriverActionLoading(false);
    }
  };

  const handleRemoveDriver = async (driverId: number) => {
    if (!selectedGroup) return;

    setDriverActionLoading(true);
    setDriversError('');

    try {
      const response = await driverGroupsService.removeDriverFromGroup(selectedGroup.id, driverId);
      if (response.success) {
        showSuccess(t('driverGroups.driverRemoved'));
        await fetchGroupDrivers(selectedGroup.id);
      } else {
        setDriversError(response.error || 'Failed to remove driver');
      }
    } catch (error) {
      setDriversError('Network error. Please try again.');
    } finally {
      setDriverActionLoading(false);
    }
  };

  const handleSelectDriver = (driver: AdminDriversResponse) => {
    setDriverIdInput(driver.driver.id.toString());
    setDriverSearchQuery(driver.driver.fullName);
    setDriverSearchResults([]);
  };

  const formatDate = (timestamp: number) => new Date(timestamp).toLocaleString();

  return (
    <div className="page">
      <div className="page-header with-actions">
        <div>
          <h1>{t('driverGroups.title')}</h1>
          <p>{t('driverGroups.subtitle')}</p>
        </div>
        <div className="page-header-actions">
          <button
            type="button"
            onClick={() => navigate('/drivers')}
            className="header-action-button secondary"
          >
            {t('common.back')}
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className="header-action-button"
          >
            + {t('driverGroups.createGroup')}
          </button>
        </div>
      </div>

      <div className="page-content">
        {pageError && <div className="error-message">{pageError}</div>}

        {successMessage && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#d4edda',
              color: '#155724',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
            }}
          >
            {successMessage}
          </div>
        )}

        <div className="table-container">
          {loading ? (
            <div className="loading-container">{t('driverGroups.loadingGroups')}</div>
          ) : groups.length === 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('common.name')}</th>
                  <th>{t('driverGroups.updatedAt')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={4} className="no-data">
                    {t('driverGroups.noGroups')}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('common.name')}</th>
                  <th>{t('driverGroups.updatedAt')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id}>
                    <td>{group.id}</td>
                    <td>{group.name}</td>
                    <td>{formatDate(group.updatedAt)}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="table-action-button"
                          onClick={() => setSelectedGroup(group)}
                        >
                          {t('driverGroups.manageDrivers')}
                        </button>
                        <button
                          type="button"
                          className="table-action-button secondary"
                          onClick={() => openEditModal(group)}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          type="button"
                          className="table-action-button danger"
                          onClick={() => handleDelete(group.id)}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="table-container" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
            <h3 style={{ margin: 0 }}>{t('driverGroups.driversTitle')}</h3>
            {selectedGroup && (
              <span style={{ color: '#6c757d' }}>
                {t('driverGroups.groupLabel')}: {selectedGroup.name}
              </span>
            )}
          </div>

          {!selectedGroup ? (
            <div className="no-data" style={{ padding: '2rem' }}>
              {t('driverGroups.selectGroupHint')}
            </div>
          ) : (
            <>
              <form
                onSubmit={handleAddDriver}
                style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '360px' }}>
                  <input
                    type="text"
                    className="search-input"
                    placeholder={t('driverGroups.searchDriverPlaceholder')}
                    value={driverSearchQuery}
                    onChange={(e) => setDriverSearchQuery(e.target.value)}
                  />
                  {driverSearchLoading && (
                    <div className="search-suggestions loading">
                      {t('driverGroups.searchingDrivers')}
                    </div>
                  )}
                  {!driverSearchLoading && driverSearchResults.length > 0 && (
                    <div className="search-suggestions">
                      {driverSearchResults.map((driver) => (
                        <button
                          key={driver.driver.id}
                          type="button"
                          className="search-suggestion"
                          onClick={() => handleSelectDriver(driver)}
                        >
                          {driver.driver.id} - {driver.driver.fullName}
                        </button>
                      ))}
                    </div>
                  )}
                  {!driverSearchLoading && driverSearchError && (
                    <div className="search-suggestions error">
                      {driverSearchError}
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  className="search-input"
                  placeholder={t('driverGroups.addDriverPlaceholder')}
                  value={driverIdInput}
                  onChange={(e) => setDriverIdInput(e.target.value)}
                  style={{ maxWidth: '200px' }}
                />
                <button
                  type="submit"
                  className="search-button"
                  disabled={driverActionLoading}
                >
                  {t('driverGroups.addDriver')}
                </button>
              </form>

              {driversError && (
                <div className="error-message" style={{ marginTop: '1rem' }}>
                  {driversError}
                </div>
              )}

              {driversLoading ? (
                <div className="loading-container">{t('driverGroups.loadingDrivers')}</div>
              ) : groupDrivers.length === 0 ? (
                <div className="no-data" style={{ padding: '2rem' }}>
                  {t('driverGroups.noDriversInGroup')}
                </div>
              ) : (
                <table className="data-table" style={{ marginTop: '1rem' }}>
                  <thead>
                    <tr>
                      <th>{t('driverGroups.driverId')}</th>
                      <th>{t('drivers.fullName')}</th>
                      <th>{t('common.phone')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupDrivers.map((driver) => (
                      <tr key={driver.id}>
                        <td>{driver.id}</td>
                        <td>{driver.fullName}</td>
                        <td>{driver.phone}</td>
                        <td>
                          <button
                            type="button"
                            className="table-action-button danger"
                            onClick={() => handleRemoveDriver(driver.id)}
                            disabled={driverActionLoading}
                          >
                            {t('driverGroups.removeDriver')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingGroup ? t('driverGroups.editGroup') : t('driverGroups.createGroup')}
      >
        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="modal-form-group">
            <label htmlFor="groupName">{t('common.name')}</label>
            <input
              id="groupName"
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t('driverGroups.namePlaceholder')}
              disabled={modalLoading}
            />
          </div>
          {modalError && <div className="error-message">{modalError}</div>}
          <div className="modal-actions">
            <button type="button" className="modal-button" onClick={closeModal} disabled={modalLoading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="modal-button primary" disabled={modalLoading}>
              {editingGroup ? t('common.save') : t('common.create')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DriverGroups;
